"use client";

// ─────────────────────────────────────────────────────────────────────────────
// OnboardingFinaleModal — closing scene of the cross-page onboarding
// chain. Activates on `/?onboarding_home=finale` (set by the
// CommunityChatBuddyTour's resume handoff via sessionStorage).
//
// Four slides, advanced via "Next" / "Back":
//   1. All done — celebratory recap of what they just walked through.
//   2. Join the community — Reddit / X / Discord deep-links.
//   3. Tools you replaced — animated bars showing the retail stack
//      Xynth subsumes (Benzinga Pro, Trade Ideas, TrendSpider,
//      TradingView, Unusual Whales). Same `RETAIL_STACK` concept the
//      cancellation flow uses for the same emotional beat ("look at
//      the dollars you just freed up"), but reframed positively
//      since this is a conversion moment, not a save moment.
//   4. CTA — "Start your free trial" → opens PlansModal directly.
//
// On any close (X, dismiss, or final CTA) we strip
// `?onboarding_home=finale` so a refresh doesn't replay the modal.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useRouter, useSearchParams } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BellRing,
  CheckCircle2,
  Database,
  DollarSign,
  RefreshCcw,
  Sparkles,
  Telescope,
  TrendingUp,
  Workflow,
} from "lucide-react";
import { SiDiscord } from "react-icons/si";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUserUsage } from "@/hooks/useUserUsage";
import { cn } from "@/lib/utils";
import {
  getHomeShowcaseStage,
  HOME_SHOWCASE_TOUR_QUERY_PARAM,
} from "@/lib/onboarding-constants";
import { telemetry } from "@/lib/telemetry";

// PlansModal is heavy (Pricing page + Stripe SDKs); keep it
// dynamically imported so the finale modal doesn't block first paint
// for users who never trigger the conversion CTA.
const PlansModal = dynamic(() => import("@/components/plans-modal"), {
  ssr: false,
});

// Retail trading-tool stack the finale modal tells the user Xynth
// replaces. Same names + price points the cancellation flow uses;
// kept inline here (not imported) because (a) the cancellation
// flow's copy is privately scoped and (b) the finale messaging is
// more upbeat / forward-looking, so the `replaces` line wording is
// tweaked. If you change one of these, sanity-check the other.
const RETAIL_STACK: {
  name: string;
  yearlyUsd: number;
  monthlyLabel: string;
  logo: string | null;
  fallback: string;
  fallbackBg: string;
  /** Short line framing what Xynth does instead of this tool. */
  doesInstead: string;
}[] = [
  {
    name: "Benzinga Pro",
    yearlyUsd: 4248,
    monthlyLabel: "$354/mo",
    logo: "/logos/cancel/benzinga.png",
    fallback: "BZ",
    fallbackBg: "bg-orange-500",
    doesInstead: "Real-time news + flow alerts on demand",
  },
  {
    name: "Trade Ideas",
    yearlyUsd: 2832,
    monthlyLabel: "$236/mo",
    logo: null,
    fallback: "TI",
    fallbackBg: "bg-blue-600",
    doesInstead: "AI-ranked scanners, ad-hoc",
  },
  {
    name: "TrendSpider",
    yearlyUsd: 2136,
    monthlyLabel: "$178/mo",
    logo: "/logos/cancel/trendspider.jpg",
    fallback: "TS",
    fallbackBg: "bg-cyan-600",
    doesInstead: "Charts + backtests from a single prompt",
  },
  {
    name: "TradingView",
    yearlyUsd: 1440,
    monthlyLabel: "$120/mo",
    logo: "/logos/cancel/tradingview.png",
    fallback: "TV",
    fallbackBg: "bg-blue-500",
    doesInstead: "Screeners + charts in plain English",
  },
  {
    name: "Unusual Whales",
    yearlyUsd: 1152,
    monthlyLabel: "$96/mo",
    logo: "/logos/cancel/unusualwhales.png",
    fallback: "UW",
    fallbackBg: "bg-purple-600",
    doesInstead: "Options flow + dark pool prints",
  },
];

const TOTAL_RETAIL_YEARLY = RETAIL_STACK.reduce(
  (sum, t) => sum + t.yearlyUsd,
  0,
);
const STACK_MAX = Math.max(...RETAIL_STACK.map((t) => t.yearlyUsd));

const STEP_COUNT = 4;

export function OnboardingFinaleModal() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: usageData } = useUserUsage();
  const stage = getHomeShowcaseStage(searchParams);
  const finaleActive = stage === "finale";

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [plansOpen, setPlansOpen] = useState(false);

  // Paid users (or enterprise) hit a different terminal CTA — they
  // don't need the plans modal, they need to start using the product.
  // Tier source matches PlansModal's `currentPlan` lookup below so the
  // two reads stay in lockstep if billing ever renames its fields.
  const currentTier = (usageData?.subscription?.tier as
    | "free"
    | "plus"
    | "pro"
    | "elite"
    | undefined) ?? "free";
  const isPaid =
    Boolean(usageData?.is_enterprise) ||
    (currentTier !== "free" && currentTier !== undefined);

  // Open the modal whenever the URL transitions into the finale
  // stage. We intentionally do NOT auto-close on stage change — the
  // user closes via X / "Maybe later" / final CTA, and that close
  // also strips the URL param.
  useEffect(() => {
    if (finaleActive) {
      setOpen(true);
      setStep(1);
      telemetry.emit("onboarding.finale_modal_open", {
        is_paid: isPaid,
        current_tier: currentTier,
      });
    } else {
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finaleActive]);

  const stripQueryParam = useMemo(
    () => () => {
      try {
        const next = new URLSearchParams(searchParams?.toString() || "");
        next.delete(HOME_SHOWCASE_TOUR_QUERY_PARAM);
        const search = next.toString();
        const path = window.location.pathname + (search ? `?${search}` : "");
        router.replace(path, { scroll: false });
      } catch {
        /* URL mutation is best-effort. */
      }
    },
    [router, searchParams],
  );

  const handleClose = () => {
    telemetry.emit("onboarding.finale_dismissed", {
      step_at_dismiss: step,
      is_paid: isPaid,
    });
    setOpen(false);
    stripQueryParam();
  };

  const handleStartTrial = () => {
    // THE conversion click — most important single event in the whole
    // funnel. Emitted before we route so it lands in telemetry even if
    // PlansModal's mount or the /automations push throws.
    telemetry.emit("onboarding.finale_trial_click", {
      step,
      is_paid: isPaid,
      current_tier: currentTier,
      destination: isPaid ? "automations" : "plans_modal",
    });
    // Paid users skip the plans modal entirely and land on
    // /automations — the next natural step after the tour.
    if (isPaid) {
      setOpen(false);
      stripQueryParam();
      window.setTimeout(() => router.push("/automations"), 120);
      return;
    }
    // Free users → hand off to the conversion modal. We deliberately
    // keep the finale modal closed BEFORE opening PlansModal so the
    // two dialogs don't stack visually for a frame.
    setOpen(false);
    stripQueryParam();
    // Defer one tick so React can unmount the Dialog before the
    // PlansModal portal mounts — avoids a flash of two backdrops.
    window.setTimeout(() => setPlansOpen(true), 180);
  };

  const handleNext = () => {
    setStep((s) => {
      const next = Math.min(STEP_COUNT, s + 1);
      if (next !== s) {
        telemetry.emit("onboarding.finale_step_advance", {
          from_step: s,
          to_step: next,
          is_paid: isPaid,
        });
      }
      return next;
    });
  };
  const handleBack = () => {
    setStep((s) => Math.max(1, s - 1));
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) handleClose();
        }}
      >
        <DialogContent
          className={cn(
            "grid max-h-[calc(100dvh-20px)] w-[calc(100vw-20px)] !max-w-[calc(100vw-20px)] grid-rows-[minmax(0,1fr)_auto] gap-0 overflow-hidden rounded-xl border border-white/10 bg-[#0b0b0d] p-0 text-white sm:w-[calc(100vw-32px)] sm:!max-w-[720px] sm:rounded-2xl",
          )}
        >
          {/* Body — animated step swap. */}
          <div className="relative min-h-0 overflow-y-auto px-4 pb-3 pt-5 sm:min-h-[410px] sm:px-7 sm:pt-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="flex min-w-0 flex-col"
              >
                {step === 1 && <SlideAllDone />}
                {step === 2 && <SlideJoinCommunity />}
                {step === 3 && <SlideToolsReplaced />}
                {step === 4 && (
                  <SlideStartTrial onStart={handleStartTrial} isPaid={isPaid} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer — Back + Next / Skip controls. The CTA on step 4
              lives inside the slide itself (SlideStartTrial), so the
              footer just shows Back + a subtle "Maybe later" exit. */}
          <div className="flex items-center justify-between px-4 pb-3 pt-0 sm:px-7 sm:pb-4">
            {step === 1 ? (
              <div className="flex w-full flex-col items-stretch justify-between gap-3 rounded-lg border border-white/30 bg-white/[0.025] px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-4">
                <div className="grid min-w-0 grid-cols-[1fr_auto_1fr] items-center gap-2 sm:flex sm:gap-4">
                  <div className="min-w-0">
                    <div className="truncate text-[8.5px] font-semibold uppercase tracking-[0.1em] text-zinc-500 sm:text-[10px] sm:tracking-[0.14em]">
                      Traditional stack
                    </div>
                    <div className="mt-0.5 text-lg font-semibold tracking-tight text-red-300 sm:text-xl">
                      ~$3,900/mo
                    </div>
                  </div>
                  <div className="rounded-full border border-white/10 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-zinc-500 sm:text-[10px]">
                    vs
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[8.5px] font-semibold uppercase tracking-[0.1em] text-zinc-500 sm:text-[10px] sm:tracking-[0.14em]">
                      Xynth
                    </div>
                    <div className="mt-0.5 text-lg font-semibold tracking-tight text-emerald-300 sm:text-xl">
                      $50/mo
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleNext}
                  size="sm"
                  className="h-9 w-full shrink-0 rounded-full bg-white px-4 text-[12px] font-semibold text-black hover:bg-zinc-100 sm:w-auto sm:px-5 sm:text-[13px]"
                >
                  See the payoff
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-zinc-500 transition-colors hover:text-zinc-300"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
            )}
            {step > 1 && step < STEP_COUNT ? (
              <Button
                onClick={step === 2 ? handleStartTrial : handleNext}
                size="sm"
                className="h-8 shrink-0 rounded-full bg-white px-3 text-[11px] font-semibold text-black hover:bg-zinc-100 sm:px-4 sm:text-[12px]"
              >
                {step === 2 ? (isPaid ? "Find alpha" : "Begin your trial now") : "Next"}
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            ) : step === STEP_COUNT ? (
              <button
                type="button"
                onClick={handleClose}
                className="text-[12px] font-medium text-zinc-500 transition-colors hover:text-zinc-300"
              >
                Maybe later
              </button>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <PlansModal
        isOpen={plansOpen}
        onClose={() => setPlansOpen(false)}
        currentPlan={currentTier}
        stripeAccount={usageData?.stripe_account}
        isEnterprise={usageData?.is_enterprise}
      />
    </>
  );
}

function SlideAllDone() {
  const cards = [
    {
      title: "Build custom alerts",
      body: "Turn your own strategy into a live market signal.",
      icon: Workflow,
      replaces: [
        { name: "Unusual Whales", price: "$96/mo", logo: "/logos/cancel/unusualwhales.png", fallback: "UW", bg: "bg-purple-600" },
        { name: "Benzinga", price: "$354/mo", logo: "/logos/cancel/benzinga.png", fallback: "BZ", bg: "bg-orange-500" },
        { name: "TradingView", price: "$120/mo", logo: "/logos/cancel/tradingview.png", fallback: "TV", bg: "bg-blue-500" },
      ],
    },
    {
      title: "Deep AI research",
      body: "Screen, compare, chart, and iterate from one prompt.",
      icon: Telescope,
      replaces: [
        { name: "ChatGPT", price: "$20/mo", logo: "/ChatGPT_logo.svg", fallback: "GPT", bg: "bg-emerald-700" },
        { name: "Claude", price: "$20/mo", logo: "/claude logo.png", fallback: "C", bg: "bg-orange-700" },
        { name: "Perplexity", price: "$20/mo", logo: "/perplexity.png", fallback: "P", bg: "bg-cyan-700" },
      ],
    },
    {
      title: "Subscribe to proven alerts",
      body: "Use live workflows already built around real setups.",
      icon: BellRing,
      replaces: [
        { name: "Discord signals", price: "$1k/mo", logo: null, icon: SiDiscord, fallback: "D", bg: "bg-indigo-600" },
        { name: "TrendSpider", price: "$178/mo", logo: "/trenspider.png", fallback: "TS", bg: "bg-cyan-600" },
      ],
    },
    {
      title: "Access market data",
      body: "Pull prices, filings, charts, news, and context into one workspace.",
      icon: Database,
      replaces: [
        { name: "Bloomberg", price: "$2k/mo", logo: null, fallback: "BB", bg: "bg-black" },
        { name: "Yahoo Finance", price: "$35/mo", logo: "/yahoo finance logo.png", fallback: "YF", bg: "bg-purple-700" },
        { name: "Koyfin", price: "$70/mo", logo: "/koyfin logo.png", fallback: "K", bg: "bg-sky-700" },
      ],
    },
  ];

  return (
    <div className="flex h-full min-w-0 flex-col">
      <h2 className="mx-auto max-w-[280px] text-center text-2xl font-semibold leading-tight tracking-tight sm:max-w-none sm:text-3xl">
        Here&apos;s what you can do now.
      </h2>

      <div className="mt-4 grid min-w-0 grid-cols-1 gap-3 sm:mt-6 sm:grid-cols-2 sm:gap-3.5">
        {cards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.14 + index * 0.16, duration: 0.42, ease: "easeOut" }}
            className="min-w-0 rounded-lg border border-white/30 bg-white/[0.025] p-3 shadow-[0_10px_30px_rgba(0,0,0,0.18)] sm:p-4"
          >
            <div className="flex min-w-0 items-start gap-2.5 sm:gap-3.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center text-zinc-300 sm:h-9 sm:w-9">
                <card.icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0">
                <div className="text-[15px] font-semibold leading-tight text-zinc-100 sm:text-[16.5px]">
                  {card.title}
                </div>
                <div className="mt-0.5 text-[12px] leading-snug text-zinc-400 sm:mt-1 sm:text-[13px]">
                  {card.body}
                </div>
              </div>
            </div>
            <div className="mt-3 border-t border-white/10 pt-2 sm:mt-4">
              <div className="mb-2 text-[8.5px] font-semibold uppercase tracking-[0.12em] text-zinc-500 sm:text-[9px] sm:tracking-[0.13em]">
                Instead of paying for
              </div>
              <div className="flex min-w-0 flex-wrap gap-1.5 sm:gap-2">
                {card.replaces.map((tool) => (
                  <span
                    key={tool.name}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-white/[0.08] bg-black/35 px-2 py-1 text-[9.5px] font-medium leading-none text-zinc-300 sm:px-2.5 sm:text-[10px]"
                  >
                    {tool.logo ? (
                      <Image
                        src={tool.logo}
                        alt={`${tool.name} logo`}
                        width={16}
                        height={16}
                        className="size-3.5 shrink-0 rounded object-contain opacity-90 sm:size-4"
                      />
                    ) : "icon" in tool && tool.icon ? (
                      <tool.icon className="size-3.5 shrink-0 text-[#5865F2] sm:size-4" />
                    ) : (
                      <span
                        className={cn(
                          "flex size-3.5 shrink-0 items-center justify-center rounded text-[6.5px] font-bold text-white/90 sm:size-4 sm:text-[7px]",
                          tool.bg,
                        )}
                      >
                        {tool.fallback}
                      </span>
                    )}
                    <span className="min-w-0 truncate">{tool.name}</span>
                    <span className="shrink-0 font-mono text-[8.5px] font-bold text-white sm:text-[9px]">
                      {tool.price}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─── Slide 2: Join Community ───────────────────────────────────────────────
function SlideJoinCommunity() {
  const milestones = [
    {
      day: "Day 1",
      title: "Subscribe to signals",
      icon: BellRing,
      cx: 88,
      cy: 194,
      left: "12%",
      top: "61%",
    },
    {
      day: "Day 3",
      title: "Run AI research",
      icon: Telescope,
      cx: 210,
      cy: 172,
      left: "30%",
      top: "53%",
    },
    {
      day: "Day 7",
      title: "Build your alert",
      icon: Workflow,
      cx: 336,
      cy: 136,
      left: "49%",
      top: "39%",
    },
    {
      day: "Day 14",
      title: "Start placing trades",
      icon: TrendingUp,
      cx: 448,
      cy: 104,
      left: "66%",
      top: "29%",
    },
    {
      day: "Day 21",
      title: "Review + iterate trades",
      icon: RefreshCcw,
      cx: 526,
      cy: 82,
      left: "78%",
      top: "21%",
    },
    {
      day: "Day 30",
      title: "Profit",
      icon: DollarSign,
      cx: 600,
      cy: 48,
      left: "88%",
      top: "22%",
    },
  ];

  return (
    <div className="flex h-full min-w-0 flex-col">
      <h2 className="text-center text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
        Your journey with Xynth.
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-center text-[13px] leading-relaxed text-zinc-400 sm:text-[14px]">
        Xynth pays for itself. Most users make 3x their subscription back
        in their first month.
      </p>

      <div className="mt-4 sm:mt-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16, duration: 0.42, ease: "easeOut" }}
          className="relative overflow-hidden rounded-xl bg-[radial-gradient(circle_at_80%_10%,rgba(52,211,153,0.14),transparent_34%),rgba(0,0,0,0.18)]"
        >
          <div className="relative">
            <svg
              viewBox="0 0 640 260"
              className="h-[190px] w-full sm:h-[260px]"
              role="img"
              aria-label="First month Xynth roadmap toward a three times return"
            >
              <defs>
                <linearGradient id="xynthRoadmapArea" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgb(52,211,153)" stopOpacity="0.42" />
                  <stop offset="100%" stopColor="rgb(52,211,153)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <line x1="28" x2="28" y1="44" y2="218" stroke="rgba(255,255,255,0.14)" />
              <line x1="28" x2="620" y1="214" y2="214" stroke="rgba(255,255,255,0.08)" />
              <line x1="28" x2="620" y1="160" y2="160" stroke="rgba(248,113,113,0.72)" strokeDasharray="5 7" strokeWidth="1.5" />
              <line x1="28" x2="620" y1="106" y2="106" stroke="rgba(255,255,255,0.07)" strokeDasharray="3 7" />
              <line x1="28" x2="620" y1="52" y2="52" stroke="rgba(255,255,255,0.07)" strokeDasharray="3 7" />
              <text x="2" y="216" fill="rgb(82,82,91)" fontSize="10">0x</text>
              <text x="2" y="163" fill="rgb(82,82,91)" fontSize="10">1x</text>
              <text x="2" y="109" fill="rgb(82,82,91)" fontSize="10">2x</text>
              <text x="2" y="55" fill="rgb(82,82,91)" fontSize="10">3x</text>

              <motion.text
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35, duration: 0.35, ease: "easeOut" }}
                x="34"
                y="151"
                fill="rgb(252,165,165)"
                fontSize="12"
                fontWeight="800"
              >
                $50 cost
              </motion.text>
              <motion.text
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.04, duration: 0.4, ease: "easeOut" }}
                x="486"
                y="30"
                fill="rgb(110,231,183)"
                fontSize="12"
                fontWeight="800"
              >
                300% return
              </motion.text>

              <motion.path
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.45, ease: "easeOut" }}
                d="M28 210 C66 201 96 188 132 194 C162 199 178 185 210 172 C246 158 274 164 306 149 C336 135 356 126 382 134 C414 144 424 114 448 104 C486 88 504 95 526 82 C556 62 588 56 620 48 L620 224 L28 224 Z"
                fill="url(#xynthRoadmapArea)"
              />
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.32, duration: 1.15, ease: "easeInOut" }}
                d="M28 210 C66 201 96 188 132 194 C162 199 178 185 210 172 C246 158 274 164 306 149 C336 135 356 126 382 134 C414 144 424 114 448 104 C486 88 504 95 526 82 C556 62 588 56 620 48"
                fill="none"
                stroke="rgb(52,211,153)"
                strokeLinecap="round"
                strokeWidth="4"
              />

              {milestones.map((item, index) => (
                <motion.circle
                  key={item.day}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    delay: 0.58 + index * 0.11,
                    duration: 0.28,
                    ease: "easeOut",
                  }}
                  cx={item.cx}
                  cy={item.cy}
                  r={index === milestones.length - 1 ? 7 : 5.5}
                  fill={index === 1 ? "rgb(251,191,36)" : "rgb(52,211,153)"}
                  stroke="white"
                  strokeWidth="2.5"
                />
              ))}

              <text x="28" y="246" fill="rgb(113,113,122)" fontSize="11">Start</text>
              <text x="188" y="246" fill="rgb(113,113,122)" fontSize="11">Break-even</text>
              <text x="564" y="246" fill="rgb(113,113,122)" fontSize="11">Day 30</text>
            </svg>

            {milestones.map((item, index) => (
              <motion.div
                key={item.day}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.68 + index * 0.1,
                  duration: 0.3,
                  ease: "easeOut",
                }}
                className="absolute hidden -translate-x-1/2 sm:block"
                style={{ left: item.left, top: item.top }}
              >
                <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/75 px-2 py-1 shadow-[0_8px_20px_rgba(0,0,0,0.28)] backdrop-blur">
                  <item.icon className="h-3 w-3 text-zinc-300" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-zinc-300">
                    {item.day}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-1 grid grid-cols-1 gap-1.5 sm:mt-1.5 sm:grid-cols-6">
            {milestones.map((item) => (
              <div
                key={item.day}
                className="flex items-center gap-2 rounded-lg bg-white/[0.035] px-2 py-1.5 sm:min-h-[72px] sm:flex-col sm:items-start sm:px-2 sm:py-2"
              >
                <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white/[0.04] text-zinc-300 ring-1 ring-white/10 sm:size-7">
                  <item.icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                    {item.day}
                  </div>
                  <div className="text-[11.5px] font-medium leading-tight text-zinc-200 sm:text-[12px]">
                    {item.title}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ─── Slide 3: Tools Replaced ───────────────────────────────────────────────
function SlideToolsReplaced() {
  return (
    <div className="flex h-full min-w-0 flex-col">
      <div className="flex items-center gap-2 self-start rounded-full border border-emerald-400/20 bg-emerald-400/[0.06] px-2.5 py-0.5">
        <Sparkles className="h-3 w-3 text-emerald-300" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
          Stack replacement
        </span>
      </div>
      <h2 className="mt-3 text-lg font-semibold leading-tight tracking-tight sm:text-xl">
        One seat replaces{" "}
        <span className="text-emerald-300">
          ${TOTAL_RETAIL_YEARLY.toLocaleString()}
        </span>
        <span className="text-zinc-500"> / yr of retail tools.</span>
      </h2>
      <p className="mt-1.5 text-[12px] leading-relaxed text-zinc-500 sm:text-[12.5px]">
        These are the screens our power users used to pay for. Xynth
        bundles the same edges into one chat-driven workflow.
      </p>

      <div className="mt-4 space-y-2.5 rounded-xl border border-white/10 bg-white/[0.02] p-2.5 sm:p-3">
        {RETAIL_STACK.map((tool, idx) => {
          const pct = (tool.yearlyUsd / STACK_MAX) * 100;
          return (
            <div key={tool.name} className="space-y-1">
              <div className="flex items-center gap-2">
                {tool.logo ? (
                  <Image
                    src={tool.logo}
                    alt={`${tool.name} logo`}
                    width={16}
                    height={16}
                    className="size-4 shrink-0 rounded object-contain"
                  />
                ) : (
                  <div
                    className={cn(
                      "flex size-4 shrink-0 items-center justify-center rounded text-[8px] font-bold text-white",
                      tool.fallbackBg,
                    )}
                  >
                    {tool.fallback}
                  </div>
                )}
                <span className="flex-1 truncate text-[12px] font-medium text-zinc-200">
                  {tool.name}
                </span>
                <span className="text-[12px] tabular-nums text-zinc-300">
                  ${tool.yearlyUsd.toLocaleString()}
                  <span className="ml-0.5 text-[9px] text-zinc-600">/yr</span>
                </span>
              </div>
              <div className="ml-6 space-y-0.5">
                <div className="h-1 overflow-hidden rounded-full bg-white/[0.04]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{
                      duration: 0.7,
                      delay: idx * 0.07,
                      ease: "easeOut",
                    }}
                    className="h-full rounded-full bg-gradient-to-r from-zinc-500/60 to-zinc-400/80"
                  />
                </div>
                <div className="text-[10.5px] leading-[14px] text-zinc-500">
                  <span className="text-emerald-300/90">Xynth does:</span>{" "}
                  {tool.doesInstead}
                </div>
              </div>
            </div>
          );
        })}

        {/* Total + Xynth row */}
        <div className="mt-2 flex items-center justify-between border-t border-dashed border-white/10 pt-2">
          <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">
            Total retail stack
          </span>
          <span className="font-mono text-[12.5px] font-semibold tabular-nums text-white">
            = ${TOTAL_RETAIL_YEARLY.toLocaleString()} / yr
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Slide 4: Final CTA ────────────────────────────────────────────────────
function SlideStartTrial({
  onStart,
  isPaid,
}: {
  onStart: () => void;
  isPaid: boolean;
}) {
  return (
    <div className="flex h-full min-w-0 flex-col items-center text-center">
      <div className="mt-1 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400/40 to-emerald-500/10 ring-1 ring-emerald-400/50 sm:mt-2 sm:h-14 sm:w-14">
        <Sparkles className="h-5 w-5 text-emerald-300 sm:h-6 sm:w-6" />
      </div>
      <h2 className="mt-3 text-lg font-semibold leading-tight tracking-tight sm:mt-4 sm:text-xl">
        {isPaid ? "Put it to work." : "Ready to put it to work?"}
      </h2>
      <p className="mt-2 max-w-sm text-[12.5px] leading-relaxed text-zinc-400 sm:text-[13.5px]">
        {isPaid
          ? "You're already in. Hop into automations, pick a signal, or build your own alert in chat."
          : "Pick a plan and Xynth runs whatever you ask. Alerts, trade write-ups, scanners, the lot. Cancel anytime."}
      </p>

      <div className="mt-4 grid w-full grid-cols-1 gap-2 sm:mt-5 sm:grid-cols-2">
        {[
          "Live signals + scheduled automations",
          "Real-time options flow + dark pool prints",
          "Backtests + dashboards from a prompt",
          "All shelves you just toured, unlocked",
        ].map((item) => (
          <div
            key={item}
            className="flex items-start gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-left"
          >
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-300" />
            <span className="text-[12px] leading-snug text-zinc-300">
              {item}
            </span>
          </div>
        ))}
      </div>

      <Button
        onClick={onStart}
        className={cn(
          "mt-5 h-10 w-full rounded-xl px-4 text-[13px] font-semibold sm:mt-6 sm:h-11 sm:px-5 sm:text-[14px]",
          "bg-gradient-to-b from-emerald-400 to-emerald-500 text-emerald-950",
          "shadow-[0_8px_24px_-6px_rgba(16,185,129,0.55)]",
          "hover:from-emerald-300 hover:to-emerald-400",
        )}
      >
        {isPaid ? "Find alpha" : "See plans + start free trial"}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}
